import React, { useEffect, useRef } from 'react';
import {
  RiMapPinLine,
  RiMapPin2Fill,
  RiUserLine,
  RiSettings4Line,
  RiWifiLine,
  RiTelegramLine,
  RiContactsLine,
  RiUserAddLine,
  RiStarLine,
  RiStarFill,
  RiDeleteBinLine,
} from 'react-icons/ri';
import { SiteContactForm, ContactType, ContactTypeLabels } from '../../services/siteService';
import type { Company } from '../../services/companyService';
import SearchableSelect from '../ui/SearchableSelect';
import ValidatedInput from '../ui/ValidatedInput';
import ValidatedTextarea from '../ui/ValidatedTextarea';
import SiteAppCredentialsSection, { SiteAppCredentialsHandle } from './SiteAppCredentialsSection';
import type { SiteFormState } from './siteForm';

/**
 * ช่องกรอกของฟอร์มโครงการ — ยกจาก renderSiteForm ของโมดัลใน SitePage (28 ต.ค. 2026) ช่องเท่าเดิมทุกช่อง
 * ใช้โดย SiteFormPage (/master-data/sites/new · /:id/edit) · หน้าดู (SiteViewPage) แสดงแบบอ่านอย่างเดียวแยกเอง
 *
 * - รหัสโครงการ disabled ทุกโหมด (immutable · ออกอัตโนมัติ) · codeError = เตือนรหัสซ้ำที่ออกใหม่
 * - รหัส 3 คอลัมน์: ช่อง "รหัสลูกค้า" = customer_number · "รหัส Activate" = activation_code (ดู siteForm.ts)
 * - project_phone = ข้อความอิสระ (ห้ามใส่ strict phone validator — DATABASE_SCHEMA.md sites)
 */
export interface SiteFormFieldsProps {
  mode: 'create' | 'edit';
  /** id ของโครงการ (โหมดแก้ไข) — บัญชีเข้าระบบแอพโหลดจาก endpoint แยก */
  siteId?: number;
  formData: SiteFormState;
  setFormData: (next: SiteFormState) => void;
  contacts: SiteContactForm[];
  setContacts: (next: SiteContactForm[]) => void;
  formErrors: Record<string, string>;
  setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  codeError: string;
  companies: Company[];
  companiesLoading: boolean;
  companiesError: boolean;
  canManageAppCredentials: boolean;
  appCredRef: React.Ref<SiteAppCredentialsHandle>;
}

const SiteFormFields: React.FC<SiteFormFieldsProps> = ({
  mode, siteId, formData, setFormData, contacts, setContacts, formErrors, setFormErrors, codeError,
  companies, companiesLoading, companiesError, canManageAppCredentials, appCredRef,
}) => {
  // KD3 S2490 (NFR-MD-009): เพิ่ม/ลบแถวผู้ติดต่อด้วยคีย์บอร์ด — เดิมเพิ่มแล้วโฟกัสค้างที่ปุ่ม (แถวใหม่อยู่ข้างบน ต้อง
  // Shift+Tab ย้อน) และลบแล้วปุ่มที่โฟกัสอยู่หายไป → โฟกัสหลุดไป body · จำ index ที่ต้องโฟกัสไว้ แล้วโฟกัสหลัง render
  const addContactButtonRef = useRef<HTMLButtonElement>(null);
  const pendingContactFocusRef = useRef<number | null>(null);
  const focusContactRow = (index: number) => { pendingContactFocusRef.current = index; };
  useEffect(() => {
    const target = pendingContactFocusRef.current;
    if (target === null) return;
    pendingContactFocusRef.current = null;
    if (target < 0) { addContactButtonRef.current?.focus(); return; }
    document.querySelector<HTMLElement>(`[data-contact-first="${target}"]`)?.focus();
  }, [contacts]);

  return (
    <div className="space-y-6">
      {/* Section: ข้อมูลหลัก */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
          <div className="w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center shadow-sm">
            <RiMapPinLine className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">ข้อมูลโครงการ</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* รหัสโครงการ - readonly ในทุกโหมด */}
          <div>
            <ValidatedInput
              label="รหัสโครงการ"
              name="code"
              value={formData.code}
              onChange={(e) => setFormData({...formData, code: e.target.value})}
              error={formErrors.code}
              required
              disabled={true}
              placeholder="รหัสจะสร้างอัตโนมัติ"
            />
            {codeError && (
              <div className="mt-2 p-2.5 bg-warning-50 border border-warning-200 rounded-lg">
                <p className="text-sm text-warning-700 flex items-center gap-2">
                  <span className="flex-shrink-0 w-4 h-4 bg-warning-200 rounded-full flex items-center justify-center">
                    <span className="text-warning-700 text-xs">!</span>
                  </span>
                  {codeError}
                </p>
              </div>
            )}
          </div>

          <ValidatedInput
            label="ชื่อโครงการ"
            name="name"
            value={formData.name}
            onChange={(e) => {
              setFormData({...formData, name: e.target.value});
              if (formErrors.name) setFormErrors({...formErrors, name: ''});
            }}
            error={formErrors.name}
            required
            placeholder="ใส่ชื่อโครงการ"
            maxLength={200}
          />
        </div>

        {/* ชื่อใช้งานจริงที่ Activate ในระบบ - แสดงเต็มความกว้าง */}
        <ValidatedInput
          label="ชื่อใช้งานจริงที่ Activate ในระบบ"
          name="display_name"
          value={formData.display_name}
          error={formErrors.display_name}
          onChange={(e) => setFormData({...formData, display_name: e.target.value})}
          placeholder="ชื่อที่ Activate ในระบบ (ถ้าต่างจากชื่อโครงการ)"
          maxLength={500}
        />

        {/* ชื่อที่ใช้ในแอพ */}
        <ValidatedInput
          label="ชื่อที่ใช้ในแอพ"
          name="app_name"
          value={formData.app_name}
          error={formErrors.app_name}
          onChange={(e) => setFormData({...formData, app_name: e.target.value})}
          placeholder="ชื่อที่แสดงในแอพพลิเคชัน"
          maxLength={100}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              บริษัท <span className="text-danger-500">*</span>
            </label>
            <SearchableSelect
              options={companies.map(company => ({
                value: company.id,
                label: company.name
              }))}
              value={formData.company_id || ''}
              onChange={(value) => {
                setFormData({ ...formData, company_id: value ? parseInt(value.toString()) : '' });
                // Clear error when user selects a value
                if (value && formErrors.company_id) {
                  setFormErrors(prev => ({ ...prev, company_id: '' }));
                }
              }}
              placeholder="เลือกบริษัท"
              searchPlaceholder="ค้นหาชื่อบริษัท..."
              required
              // KD3 S2494 (NEG-MD-016): ระหว่างรอ/โหลดรายชื่อบริษัทไม่สำเร็จ dropdown เคยบอก "ไม่พบข้อมูล"
              // เหมือนระบบไม่มีบริษัทเลย · error เคยเป็น <p> แยกที่ไม่ผูกกับช่อง (ไม่มี aria-invalid/describedby)
              emptyMessage={
                companiesLoading
                  ? 'กำลังโหลดรายชื่อบริษัท...'
                  : companiesError
                    ? 'โหลดรายชื่อบริษัทไม่สำเร็จ — ปิดฟอร์มแล้วเปิดใหม่อีกครั้ง'
                    : undefined
              }
              error={formErrors.company_id || undefined}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">สถานะ</label>
            <select
              aria-label="สถานะ"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="select disabled:bg-secondary-50 disabled:text-secondary-500"
            >
              <option value="active">ใช้งาน</option>
              <option value="inactive">ไม่ใช้งาน</option>
            </select>
          </div>
        </div>
      </div>

      {/* Section: ที่อยู่และ Activate Code */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
          <div className="w-8 h-8 bg-info-600 rounded-md flex items-center justify-center shadow-sm">
            <RiMapPin2Fill className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">ที่อยู่และรหัส Activate</h3>
        </div>

        <ValidatedTextarea
          label="ที่อยู่"
          name="address"
          value={formData.address}
          onChange={(e) => setFormData({...formData, address: e.target.value})}
          error={formErrors.address}
          placeholder="ใส่ที่อยู่โครงการ"
          rows={3}
          maxLength={500}
        />

        {/* รหัส Activate */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ValidatedInput
            label="รหัสลูกค้า"
            name="customer_number"
            value={formData.customer_number}
            error={formErrors.customer_number}
            onChange={(e) => setFormData({...formData, customer_number: e.target.value})}
            placeholder="รหัสลูกค้า (ถ้ามี)"
            // KD3 S2492: คอลัมน์ customer_number varchar(50) / schema max_length 50 — เดิม 100 พิมพ์เกินแล้วโดน 422
            maxLength={50}
          />
          <ValidatedInput
            label="รหัส Activate"
            name="activation_code"
            value={formData.activation_code}
            error={formErrors.activation_code}
            onChange={(e) => setFormData({...formData, activation_code: e.target.value})}
            placeholder="รหัส Activate สำหรับเปิดใช้งาน"
            maxLength={50}
          />
        </div>
      </div>

      {/* Section: บัญชีเข้าระบบแอพ (อีเมล + รหัสผ่าน · PO 30 ก.ย. 2026) */}
      <SiteAppCredentialsSection
        ref={appCredRef}
        siteId={siteId}
        mode={mode}
        canManage={canManageAppCredentials}
      />

      {/* Section: ข้อมูลผู้ติดต่อ */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
          <div className="w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center shadow-sm">
            <RiUserLine className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">ข้อมูลผู้ติดต่อ</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ValidatedInput
            label="ผู้ติดต่อ"
            name="contactPerson"
            value={formData.contactPerson}
            error={formErrors.contact_person}
            onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
            placeholder="ชื่อผู้ติดต่อ"
            maxLength={100}
          />

          <ValidatedInput
            label="เบอร์โทรศัพท์"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            error={formErrors.phone}
            placeholder="0x-xxxx-xxxx"
            maxLength={15}
          />
        </div>

        <ValidatedInput
          label="อีเมล"
          name="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          error={formErrors.email}
          placeholder="example@project.com"
          maxLength={100}
        />
      </div>

      {/* Section: การตั้งค่าและเปิดใช้งาน */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
          <div className="w-8 h-8 bg-success-600 rounded-md flex items-center justify-center shadow-sm">
            <RiSettings4Line className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">การตั้งค่าและเปิดใช้งาน</h3>
        </div>

        {/* Feature toggles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
            formData.k_connect_enabled
              ? 'border-primary-500 bg-primary-50'
              : 'border-secondary-200 bg-white hover:border-secondary-300'
          }`}>
            <input
              type="checkbox"
              checked={formData.k_connect_enabled}
              onChange={(e) => setFormData({...formData, k_connect_enabled: e.target.checked})}
              className="w-5 h-5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
            />
            <div className="flex items-center gap-2">
              <RiWifiLine className={`w-5 h-5 ${formData.k_connect_enabled ? 'text-primary-600' : 'text-secondary-400'}`} />
              <div>
                <span className={`font-medium ${formData.k_connect_enabled ? 'text-primary-700' : 'text-secondary-700'}`}>
                  K-Connect
                </span>
                <p className="text-xs text-secondary-500">เปิดใช้งานระบบ K-Connect</p>
              </div>
            </div>
          </label>

          <label className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
            formData.telegram_enabled
              ? 'border-info-500 bg-info-50'
              : 'border-secondary-200 bg-white hover:border-secondary-300'
          }`}>
            <input
              type="checkbox"
              checked={formData.telegram_enabled}
              onChange={(e) => setFormData({...formData, telegram_enabled: e.target.checked})}
              className="w-5 h-5 rounded border-secondary-300 text-info-600 focus:ring-info-500 disabled:opacity-50"
            />
            <div className="flex items-center gap-2">
              <RiTelegramLine className={`w-5 h-5 ${formData.telegram_enabled ? 'text-info-600' : 'text-secondary-400'}`} />
              <div>
                <span className={`font-medium ${formData.telegram_enabled ? 'text-info-700' : 'text-secondary-700'}`}>
                  Telegram
                </span>
                <p className="text-xs text-secondary-500">รับแจ้งเตือนผ่าน Telegram</p>
              </div>
            </div>
          </label>
        </div>

        <ValidatedInput
          label="เบอร์โทรโครงการ/นิติบุคคล"
          name="project_phone"
          type="tel"
          error={formErrors.project_phone}
          value={formData.project_phone}
          onChange={(e) => setFormData({...formData, project_phone: e.target.value})}
          placeholder="เช่น 0812345678 หรือ หน.รปภ สมชาย 0812345678"
          maxLength={200}
        />

        <ValidatedTextarea
          label="ข้อมูลติดต่อ รปภ./ผู้ดูแล"
          name="contact_notes"
          value={formData.contact_notes}
          error={formErrors.contact_notes}
          onChange={(e) => setFormData({...formData, contact_notes: e.target.value})}
          placeholder="ข้อมูลเพิ่มเติมสำหรับติดต่อ รปภ. หรือผู้ดูแลโครงการ"
          rows={3}
          maxLength={500}
        />
      </div>

      {/* Section: รายชื่อผู้ติดต่อโครงการ */}
      <div className="space-y-4">
        <div className="flex items-center justify-between pb-2 border-b border-secondary-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-warning-600 rounded-md flex items-center justify-center shadow-sm">
              <RiContactsLine className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-secondary-800">รายชื่อผู้ติดต่อโครงการ</h3>
              <p className="text-xs text-secondary-500">รายชื่อ รปภ., นิติบุคคล, ผู้จัดการ ฯลฯ</p>
            </div>
          </div>
          {(
            <button
              type="button"
              ref={addContactButtonRef}
              onClick={() => {
                focusContactRow(contacts.length);
                setContacts([...contacts, {
                  contact_type: ContactType.SECURITY,
                  name: '',
                  phone: '',
                  line_id: '',
                  notes: '',
                  is_primary: contacts.length === 0
                }]);
              }}
              className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg border border-primary-200 transition-colors"
            >
              <RiUserAddLine className="w-3.5 h-3.5 mr-1" />
              เพิ่มผู้ติดต่อ
            </button>
          )}
        </div>

        {/* Empty State */}
        {contacts.length === 0 && (
          <div className="p-6 border-2 border-dashed border-secondary-200 rounded-xl text-center">
            <RiContactsLine className="w-10 h-10 mx-auto text-secondary-300 mb-2" />
            <p className="text-sm text-secondary-500">ยังไม่มีรายชื่อผู้ติดต่อ</p>
            {(
              <p className="text-xs text-secondary-400 mt-1">คลิก "เพิ่มผู้ติดต่อ" เพื่อเริ่มเพิ่มรายชื่อ</p>
            )}
          </div>
        )}

        {/* Contacts List */}
        {contacts.map((contact, index) => (
          <div
            key={index}
            className={`p-4 rounded-xl border-2 transition-colors ${
              contact.is_primary
                ? 'border-warning-300 bg-warning-50/50'
                : 'border-secondary-200 bg-white'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 text-xs font-medium bg-secondary-100 text-secondary-600 rounded">
                  #{index + 1}
                </span>
                {contact.is_primary && (
                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-warning-100 text-warning-700 rounded">
                    <RiStarFill className="w-3 h-3 mr-1" />
                    ผู้ติดต่อหลัก
                  </span>
                )}
              </div>
              {(
                <div className="flex items-center gap-1">
                  {!contact.is_primary && (
                    <button
                      type="button"
                      onClick={() => {
                        const newContacts = contacts.map((c, i) => ({
                          ...c,
                          is_primary: i === index
                        }));
                        // ปุ่มดาวของแถวนี้จะหายไปเมื่อกลายเป็นผู้ติดต่อหลัก → ย้ายโฟกัสไปช่องแรกของแถวแทนการหลุดไป body
                        focusContactRow(index);
                        setContacts(newContacts);
                      }}
                      // KD3 S2490 (NFR-MD-003/009): 28px กดยากบนมือถือ → ≥44px ใต้ breakpoint sm · ปุ่มไอคอนต้องมีชื่อ
                      // ที่บอกว่าเป็นผู้ติดต่อคนไหน (เดิม title ซ้ำกันทุกแถว screen reader แยกไม่ออก)
                      className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 p-1.5 text-secondary-400 hover:text-warning-600 hover:bg-warning-50 rounded transition-colors"
                      title="ตั้งเป็นผู้ติดต่อหลัก"
                      aria-label={`ตั้งผู้ติดต่อ #${index + 1}${contact.name ? ` (${contact.name})` : ''} เป็นผู้ติดต่อหลัก`}
                    >
                      <RiStarLine className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const newContacts = contacts.filter((_, i) => i !== index);
                      // ถ้าลบ primary แล้วยังมี contacts อยู่ ให้ตั้ง index 0 เป็น primary
                      if (contact.is_primary && newContacts.length > 0) {
                        newContacts[0].is_primary = true;
                      }
                      focusContactRow(newContacts.length ? Math.max(0, index - 1) : -1);
                      setContacts(newContacts);
                    }}
                    className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 p-1.5 text-secondary-400 hover:text-danger-600 hover:bg-danger-50 rounded transition-colors"
                    title="ลบผู้ติดต่อ"
                    aria-label={`ลบผู้ติดต่อ #${index + 1}${contact.name ? ` (${contact.name})` : ''}`}
                  >
                    <RiDeleteBinLine className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-secondary-600 mb-1">ประเภทผู้ติดต่อ</label>
                <select
                  aria-label={`ประเภทผู้ติดต่อ #${index + 1}`}
                  data-contact-first={index}
                  value={contact.contact_type}
                  onChange={(e) => {
                    const newContacts = [...contacts];
                    newContacts[index].contact_type = e.target.value as ContactType;
                    setContacts(newContacts);
                  }}
                  className="select text-sm disabled:bg-secondary-50 disabled:text-secondary-500"
                >
                  {Object.values(ContactType).map(type => (
                    <option key={type} value={type}>{ContactTypeLabels[type]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-secondary-600 mb-1">ชื่อผู้ติดต่อ</label>
                <input
                  aria-label="ชื่อผู้ติดต่อ"
                  type="text"
                  value={contact.name || ''}
                  onChange={(e) => {
                    const newContacts = [...contacts];
                    newContacts[index].name = e.target.value;
                    setContacts(newContacts);
                  }}
                  placeholder="ใส่ชื่อผู้ติดต่อ"
                  maxLength={100}
                  className="input text-sm disabled:bg-secondary-50 disabled:text-secondary-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-secondary-600 mb-1">เบอร์โทร</label>
                {/* feedback ทิวากร 3 ก.ย. 2026: server ตรวจรูปแบบเบอร์ของผู้ติดต่อแต่ละแถว
                    (422 field "contacts -> N -> phone") แต่จอไม่เคยแสดงว่าแถวไหนผิด
                    → ผู้ใช้เห็นแต่ toast รวม · ผูก error ของแถวนี้มาแสดงใต้ช่อง */}
                <input
                  type="tel"
                  name={`contacts.${index}.phone`}
                  value={contact.phone || ''}
                  onChange={(e) => {
                    const newContacts = [...contacts];
                    newContacts[index].phone = e.target.value;
                    setContacts(newContacts);
                    if (formErrors[`contacts.${index}.phone`]) {
                      setFormErrors(prev => ({ ...prev, [`contacts.${index}.phone`]: '' }));
                    }
                  }}
                  placeholder="0x-xxxx-xxxx"
                  maxLength={20}
                  aria-invalid={Boolean(formErrors[`contacts.${index}.phone`])}
                  aria-describedby={formErrors[`contacts.${index}.phone`] ? `contacts-${index}-phone-error` : undefined}
                  className={`input text-sm disabled:bg-secondary-50 disabled:text-secondary-500 ${formErrors[`contacts.${index}.phone`] ? 'border-danger-500' : ''}`}
                />
                {formErrors[`contacts.${index}.phone`] && (
                  <p id={`contacts-${index}-phone-error`} className="mt-1 text-xs text-danger-600">
                    {formErrors[`contacts.${index}.phone`]}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-secondary-600 mb-1">Line ID</label>
                <input
                  aria-label="Line ID"
                  type="text"
                  value={contact.line_id || ''}
                  onChange={(e) => {
                    const newContacts = [...contacts];
                    newContacts[index].line_id = e.target.value;
                    setContacts(newContacts);
                  }}
                  placeholder="@line_id"
                  maxLength={50}
                  className="input text-sm disabled:bg-secondary-50 disabled:text-secondary-500"
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-xs font-medium text-secondary-600 mb-1">หมายเหตุ</label>
              <input
                aria-label="หมายเหตุ"
                type="text"
                value={contact.notes || ''}
                onChange={(e) => {
                  const newContacts = [...contacts];
                  newContacts[index].notes = e.target.value;
                  setContacts(newContacts);
                }}
                placeholder="หมายเหตุเพิ่มเติม เช่น เวลาทำงาน"
                maxLength={200}
                className="input text-sm disabled:bg-secondary-50 disabled:text-secondary-500"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SiteFormFields;
